/**
 * EmptyState — shared empty-list visual for entity pages.
 *
 * Used wherever a Models / Trajectories / Datasets / Labels list returns zero rows
 * or the user needs a primary CTA to populate state.
 */

import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface EmptyStateProps {
  icon?: LucideIcon
  title: string
  description?: string
  action?: React.ReactNode
  className?: string
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-border px-6 py-12 text-center',
        className
      )}
    >
      {Icon && (
        <Icon className="mb-4 h-10 w-10 text-muted-foreground" aria-hidden="true" />
      )}
      <h3 className="font-serif text-base font-medium text-foreground">{title}</h3>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {/* max-w-full: a flex item sized to its content never shrinks below a
          code block's longest line, so a curl snippet would spill past the
          border on a phone instead of scrolling inside it. */}
      {action && <div className="mt-6 max-w-full">{action}</div>}
    </div>
  )
}
